import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Box,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Card,
  CardContent,
  CardHeader,
  LinearProgress,
  CircularProgress,
  Paper,
  Tabs,
  Tab,
  Divider,
  useMediaQuery
} from '@mui/material';

import ReactGA from "react-ga4";

import SummaryOverall from './Components/SummaryOverall';
import VegetationFuel from './Components/VegetationFuel';
import IntroSummary from './Components/IntroSummary';

import SummaryFireRisk from './Components/SummaryFireRisk';
import SummaryFuelRisk from './Components/SummaryFuelRisk';

const TRACKING_ID = "GT-NS8CZ5MZ";
ReactGA.initialize(TRACKING_ID);

// Constants
const Maps_API_KEY = 'AIzaSyC_j9tMBT-IjQkmPp886oU5XCYww14XNtI';
const PROD_CONTENT_BASE_URL = 'https://content.firerebuild.com';
const PROD_APP_BASE_URL = 'https://app.firerebuild.com';
const BURN_CHECK_DATA_BASE_PATH = `${PROD_CONTENT_BASE_URL}/burn-check/data/codes`;
const REPORT_DATA_BASE_URL = `${PROD_CONTENT_BASE_URL}/data/report/fveg-gridmet`;
const CHART_PAGE_BASE_URL = `${PROD_APP_BASE_URL}/fire-risk-zone-embed/fire-environment-mapping/embed.html`;
//const CHART_PAGE_BASE_URL = 'http://127.0.0.1:8080/fire-risk-zone-embed/fire-environment-mapping/embed.html';


const iconValues = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J"];

// Haversine distance utility
const haversine = (lat1, lng1, lat2, lng2) => {
  const toRad = (angle) => (angle * Math.PI) / 180;
  const R = 6371; // Earth radius km
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const App = () => {
  const [address, setAddress] = useState('');
  const [searchStatus, setSearchStatus] = useState('');
  const [topCodes, setTopCodes] = useState([]);
  const [selectedCode, setSelectedCode] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [locationData, setLocationData] = useState(null);

  const mapRef = useRef(null);
  const autocompleteRef = useRef(null);
  const addressInputRef = useRef(null);
  const mapInstance = useRef(null);
  const markers = useRef({ user: null, circle: null, codes: [] });

  const [preloadedCode, setPreloadedCode] = useState(null);

  const isMobile = useMediaQuery('(max-width:1100px)');

  // Set SEO metadata on component mount
  useEffect(() => {
    document.title = "Fire Risk Comparative Analysis | FireRebuild.com";
    const metaDescription = document.createElement('meta');
    metaDescription.name = 'description';
    metaDescription.content = 'A comparative analysis of fire and fuel risk across California using data from the 2025 Palisades Fire. Understand your community\'s wildfire risk relative to a Very High Fire Hazard Severity Zone.';
    document.head.appendChild(metaDescription);

    return () => {
      document.head.removeChild(metaDescription);
    };
  }, []);

  useEffect(() => {
    const initMap = () => {
      const defaultCenter = { lat: 34.0321, lng: -118.7831 };
      mapInstance.current = new window.google.maps.Map(mapRef.current, {
        center: defaultCenter,
        zoom: 11,
        fullscreenControl: false,
        streetViewControl: false,
        mapTypeControl: false,
        zoomControl: true,
      });

      const defaultBounds = new window.google.maps.LatLngBounds(
        new window.google.maps.LatLng(32.5343, -124.4820), // Southwest corner of CA
        new window.google.maps.LatLng(42.0095, -114.1312)  // Northeast corner of CA
      );

      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        addressInputRef.current,
        {
          bounds: defaultBounds,
          strictBounds: true,
          componentRestrictions: { country: ['us'] },
          types: ['address'],
          fields: ['address_components', 'formatted_address', 'geometry', 'name']
        }
      );

      autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current.getPlace();
        if (!place.geometry) {
          setSearchStatus(`No details for: '${place.name}'`);
          return;
        }
        setAddress(place.formatted_address);
        handleCounty(place);
        handlePlaceSearch(place.geometry.location.lat(), place.geometry.location.lng());
      });
    };

const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${Maps_API_KEY}&libraries=places`;
    script.async = true;

    const urlParams = new URLSearchParams(window.location.search);
    const groupParam = urlParams.get('group');
    const codeParam = urlParams.get('code');

    const isValidGroup = groupParam && /^[0-9A-Fa-f]{4}$/.test(groupParam);
    const isValidCode = codeParam === 'gavin' || (codeParam && /^L[0-9A-Fa-f]{4}$/.test(codeParam));

    if (isValidGroup && isValidCode) {
      // Function to fetch and process preloaded code data
      const fetchPreloadedCodeData = async () => {
        
        // FIX: CALL INITMAP FIRST to ensure mapInstance.current is not null
        initMap(); 
        
        setReportLoading(true);
        try {
          // 1. Fetch the group file
          const filePath = `${BURN_CHECK_DATA_BASE_PATH}/${groupParam}.json`;
          
          const fileResp = await fetch(filePath);
          if (!fileResp.ok) {
            throw new Error(`Error ${fileResp.status} fetching group data`);
          }
          
          const points = await fileResp.json();
          
          // 2. Find the matching code
          const codeData = points.find(p => p.code === codeParam);
          
          if (codeData) {
            const lat = Number(codeData.lat);
            const lng = Number(codeData.lon);
            
            // 3. Perform Reverse Geocoding to get Address and County/City
            const geocodeUrl = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${Maps_API_KEY}`;
            const geocodeResponse = await fetch(geocodeUrl);
            const geocodeData = await geocodeResponse.json();
            
            let addressToSet = `Lat: ${lat.toFixed(6)}, Lng: ${lng.toFixed(6)}`;
            let placeData = null;

            if (geocodeData.status === 'OK' && geocodeData.results.length > 0) {
                const result = geocodeData.results[0];
                addressToSet = result.formatted_address;
                placeData = result; 
            }
            
            // 4. Update State and Run Searches
            
            setAddress(addressToSet);
            
            if (placeData) {
                handleCounty(placeData);
            }

            // Run the main search logic 
            handlePlaceSearch(lat, lng);
            
            const preselectedCodeData = {
                code: codeParam,
                lat: lat,
                lon: lng
            };

            // Now that initMap has run (at the start of this function), 
            // mapInstance.current is available for these calls:
            mapInstance.current.setCenter({ lat, lng });
            mapInstance.current.setZoom(11);
            
            markers.current.user = new window.google.maps.Marker({
              position: { lat, lng },
              map: mapInstance.current,
              title: `Preloaded Code: ${codeParam}`,
            });

            handleCodeSelect(preselectedCodeData);
            setPreloadedCode(preselectedCodeData);

          } else {
            console.error(`Code ${codeParam} not found in group ${groupParam}.`);
            // Map is still initialized from the call at the start of the function.
          }

        } catch (error) {
          console.error("Failed to preload data:", error);
          // Map is still initialized.
        } finally {
          if (!selectedCode) {
            setReportLoading(false);
          }
        }
      };
      
      script.onload = fetchPreloadedCodeData;
      
    } else {
      // If no valid URL params, just initialize the map normally
      script.onload = initMap;
    }

    document.head.appendChild(script);
    return () => document.head.removeChild(script);
  }, []);

  
  const handleCounty = async (place) => {

    const components = place.address_components;
    let city = '';
    let county = '';

    // Loop through components to find city (locality) and country
    for (const component of components) {
      if (component.types.includes('locality')) {
        city = component.long_name;
      }
      if (component.types.includes('administrative_area_level_2')) {
        county = component.long_name;
      }
    }

    if (city === '') {
      for (const component of components) {
        if (component.types.includes('neighborhood')) {
          city = component.long_name;
        }
      }
    }

    if (city === '') {
      city = 'Unknown';
    }
    setLocationData({city, county});
  }

  // Main search logic
  const handlePlaceSearch = async (lat, lng) => {
    setSearchStatus('Searching...');
    setTopCodes([]);
    setSelectedCode(null);
    setReportData(null);
    setTabValue(0); // Reset to the first tab

    if (markers.current.user) markers.current.user.setMap(null);
    if (markers.current.circle) markers.current.circle.setMap(null);
    markers.current.codes.forEach((m) => m.setMap(null));
    markers.current.codes = [];

    const newCenter = { lat, lng };
    mapInstance.current.setCenter(newCenter);
    mapInstance.current.setZoom(11);

    markers.current.user = new window.google.maps.Marker({
      position: newCenter,
      map: mapInstance.current,
      title: 'Searched Address',
    });

    markers.current.circle = new window.google.maps.Circle({
      center: newCenter,
      radius: 200,
      map: mapInstance.current,
      strokeColor: '#FF0000',
      fillColor: '#FF0000',
      fillOpacity: 0.35,
    });

    const latHex = Math.round(Math.abs(lat)).toString(16).padStart(2, '0');
    const lngHex = Math.round(Math.abs(lng)).toString(16).padStart(2, '0');
    const latInt = parseInt(latHex, 16);
    const lngInt = parseInt(lngHex, 16);
    const hexKeys = [];
    const minHex = parseInt('2172', 16);
    const maxHex = parseInt('2A7C', 16);

    for (let i = -1; i <= 1; i++) {
      for (let j = -1; j <= 1; j++) {
        const currentLatHex = (latInt + i).toString(16).padStart(2, '0').toUpperCase();
        const currentLngHex = (lngInt + j).toString(16).padStart(2, '0').toUpperCase();
        const hexKey = `${currentLatHex}${currentLngHex}`;
        if (parseInt(hexKey, 16) >= minHex && parseInt(hexKey, 16) <= maxHex) {
          hexKeys.push(hexKey);
        }
      }
    }

    try {
      const allPoints = [];
      const fetchPromises = hexKeys.map(async (hexKey) => {
        const filePath = `${BURN_CHECK_DATA_BASE_PATH}/${hexKey}.json`;
        try {
          const fileResp = await fetch(filePath);
          if (!fileResp.ok) {
            // Log or handle the error, but don't stop the process
            console.error(`Error ${fileResp.status} for key ${hexKey}`);
            return [];
          }
          const points = await fileResp.json();
          return points;
        } catch (error) {
          console.error(`Fetch failed for ${hexKey}:`, error);
          return [];
        }
      });

      const results = await Promise.all(fetchPromises);
      results.forEach(points => {
        allPoints.push(...points);
      });

      if (allPoints.length === 0) {
        setSearchStatus("Address must be in California.  Unless you would like to pay for this feature.");
        return;
      }


      //const distances = points.map((p) => ({
      const distances = allPoints.map((p) => ({
        ...p,
        distance: haversine(lat, lng, p.lat, p.lon) * 0.621371,
      }));
      distances.sort((a, b) => a.distance - b.distance);
      const top5 = distances.slice(0, 10);
      setTopCodes(top5);
      setSearchStatus('');

      top5.forEach((codeData, index) => {
        const marker = new window.google.maps.Marker({
          position: { lat: Number(codeData.lat), lng: Number(codeData.lon) },
          map: mapInstance.current,
          title: `Code: ${codeData.code}`,
          label: iconValues[index],
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE, // Use a built-in symbol path
            scale: 10,
            fillColor: 'yellow', // Set the fill color to yellow
            fillOpacity: 0.8,
            strokeWeight: 1,
            strokeColor: 'black',
            labelOrigin: new window.google.maps.Point(0, 0),
          },
        });
        marker.addListener('click', () => handleCodeSelect(codeData));
        markers.current.codes.push(marker);
      });
    } catch (err) {
      console.error(err);
      setSearchStatus(`Error fetching burn check data for key ${hexKeys}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!address.trim()) {
      setSearchStatus('Please enter an address.');
      return;
    }
    const geocodeUrl = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
      address
    )}&key=${Maps_API_KEY}`;
    try {
      const response = await fetch(geocodeUrl);
      const data = await response.json();
      if (data.status !== 'OK' || data.results.length === 0) {
        setSearchStatus('Address not found.');
        return;
      }
      const location = data.results[0].geometry.location;
      handlePlaceSearch(location.lat, location.lng);
    } catch (err) {
      console.error(err);
      setSearchStatus('Error during geocoding.');
    }
  };

  const handleCodeSelect = async (codeData) => {
    setSelectedCode(codeData);
    setReportData(null);
    setReportLoading(true);
    try {
      const reportUrl = `${REPORT_DATA_BASE_URL}/${codeData.code}.json`;
      const response = await fetch(reportUrl);
      if (!response.ok) throw new Error('Bad response');
      const data = await response.json();
      setReportData(data);
    } catch (err) {
      console.error(err);
      setReportData(null);
    } finally {
      setReportLoading(false);
    }
  };

  const chartUrl = selectedCode ? `${CHART_PAGE_BASE_URL}?00001&code=${selectedCode.code}` : '';

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const SummaryContent = () => {
    return (
      <>
      
      </>
    )
  };


  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>



      <IntroSummary />


    <div>


<>
      <Box sx={{ mb: 1 }}>
        <Card sx={{ mb: 1 }}>
          <Box sx={{ bgcolor: "gray", color: "white", p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h5">
                <strong>Address for Comparison</strong>
              </Typography>
            </Box>
          </Box>

          <CardContent>
            <Box component="form" onSubmit={handleSubmit} sx={{ mb: 1 }}>
              <TextField
                inputRef={addressInputRef}
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                label="Enter Full Address"
                variant="outlined"
                fullWidth
                required
              />
              <Typography variant="caption" color="text.secondary">
                Start typing to get address suggestions
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {searchStatus && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0 }}>
          {searchStatus}
        </Typography>
      )}


      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 } }>
        <Paper>
            {topCodes.length > 0 && (
              <>

              <Paper variant="outlined" sx={{border: 'none', p: 0}}>

                <Box sx={{ bgcolor: "gray", color: "white", p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h5">
                      <strong>Closest Climate Data Points</strong>
                    </Typography>
                  </Box>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 2, mb: 2 }}>
                    Select a data point to review the results
                  </Typography>
                </Box>

              </Paper>

              <Paper 
                variant="outlined"
                sx={{ 
                  p: 0,
                  height: 300, // Set a fixed height
                  overflowY: 'auto' // Enable vertical scrolling when content exceeds the height
                }}
              >
                <List>
                  {topCodes.map((c, i) => (
                    <ListItem key={c.code} disablePadding>
                      <ListItemButton
                        selected={selectedCode && selectedCode.code === c.code}
                        onClick={() => handleCodeSelect(c)}
                      >
                        <ListItemText
                          primary={`${iconValues[i]}: Distance: ${c.distance.toFixed(2)} mi`}
                          secondary={`Latitude: ${Number(c.lat).toFixed(9)} | Longitude: ${Number(c.lon).toFixed(9)}` }
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Paper>
              </>
            )}
            
            {/*
            preloadedCode && (
              <>
                <Paper variant="outlined" sx={{border: 'none', p: 0}}>

                  <Box sx={{ bgcolor: "darkgray", color: "white", p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="h5">
                        <strong>Preloaded Code Data Point</strong>
                      </Typography>
                    </Box>
                  </Box>

                </Paper>
                <Paper 
                  variant="outlined"
                  sx={{ 
                    p: 0,
                    overflowY: 'auto'
                  }}
                >
                  <List>
                      <ListItem disablePadding>
                        <ListItemButton
                          selected={selectedCode && selectedCode.code === preloadedCode.code}
                          onClick={() => handleCodeSelect(preloadedCode)}
                        >
                          <ListItemText
                            primary={`Code: ${preloadedCode.code}`}
                            secondary={`Latitude: ${Number(preloadedCode.lat).toFixed(9)} | Longitude: ${Number(preloadedCode.lon).toFixed(9)}` }
                          />
                        </ListItemButton>
                      </ListItem>
                  </List>
                </Paper>
              </>
            )
              */}
        </Paper>
        <Paper>
          <Box ref={mapRef} sx={{ height: 400, width: '100%' }} />
        </Paper>
      </Box>

      {selectedCode && reportData && setLocationData && <SummaryOverall rawData={reportData} locationData={locationData} />}

      {selectedCode && reportData && setLocationData && (
          <Box sx={{ mt: 3 }}>

            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="fire risk tabs"
              sx={{ fontSize: '1.25rem' }} // Add a general font size to the Tabs component
            >
              <Tab label="Historical Fire Risk" sx={{ fontSize: 'inherit', fontWeight: 'bold' }} />
              <Tab label="Vegetation & Fuel Risk" sx={{ fontSize: 'inherit', fontWeight: 'bold' }} />
            </Tabs>

            <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderTop: 'none' }}>
              {tabValue === 0 && (
                <Box>
                  {selectedCode ? (
                    <>
                      {selectedCode && reportData && setLocationData && <SummaryFireRisk rawData={reportData} locationData={locationData} />}
                      
                      {isMobile ? (
                        <>
                          <Box sx={{ mb: 1 }}>
                            <Card sx={{ mb: 1 }}>
                              <Box sx={{ bgcolor: "gray", color: "white", p: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Typography variant="h5">
                                    <strong>Desktop Only</strong>
                                  </Typography>
                                </Box>
                              </Box>
                              
                              <Box sx={{ mb: 1, p:2 }}>
                                <Typography variant="body1">
                                  Visit this app on a computer, not a phone.  This app contains detailed information which does not display correctly on mobile devices.
                                </Typography>
                              </Box>

                            </Card>
                          </Box>
                        </>
                      ) : (
                        <iframe
                          src={chartUrl}
                          title="Historical Fire Risk"
                          width="100%"
                          height="2200"
                          style={{ border: 'none' }}
                        />
                      )}
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Select a location data point above to view the historical fire risk.
                    </Typography>
                  )}
                </Box>
              )}
              {tabValue === 1 && (
                <Box>
                  {selectedCode ? (
                    <>
                      {selectedCode && reportData && setLocationData && <SummaryFuelRisk rawData={reportData} locationData={locationData} />}
                      <VegetationFuel codeLocation={selectedCode.code} />
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Select a location data point above to view the vegetation and fuel data.
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </Box>
      )}
</>


    </div>






    </Container>
  );
};

export default App;